import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Form, Card } from 'react-bootstrap';
import useGoogleMaps, { parseAddressComponents } from '../../hooks/useGoogleMaps';

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Search for an address...",
  className = "",
  disabled = false,
  showPreview = true
}) => {
  const containerRef = useRef(null);
  const autocompleteElementRef = useRef(null);
  const [google, setGoogle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Get Google Maps API key from environment or config
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const googleMaps = useGoogleMaps(apiKey);

  // Memoize the callback functions to prevent unnecessary re-renders
  const handlePlaceSelect = useCallback(
    async (e) => {
      try {
        // pull the prediction out of the gmp-select event
        const { placePrediction } = e;
        // convert prediction to a full Place
        const place = await placePrediction.toPlace();
        // fetch the fields you need (geometry, formatted_address, address_components)
        await place.fetchFields({
          fields: [
            'formattedAddress',
            'addressComponents',
            'displayName',
            'location'
          ]
        });

        const addressData = parseAddressComponents(place);
        addressData.latitude = place.location?.lat() ?? null;
        addressData.longitude = place.location?.lng() ?? null;
        addressData.displayName = place.displayName || '';
        addressData.formattedAddress = place.formattedAddress || '';
        const formattedAddress = place.formattedAddress || place.displayName || '';

        // update the hidden input & actual element
        if (containerRef.current) containerRef.current.value = formattedAddress;
        if (autocompleteElementRef.current) autocompleteElementRef.current.value = formattedAddress;
        if (onChange) onChange(formattedAddress);

        // set preview state - explicitly include displayName
        setSelectedPlace({
          ...place,
          displayName: place.displayName,
          formattedAddress: place.formattedAddress,
          parsedData: addressData
        });
        if (onAddressSelect) onAddressSelect(addressData);
      } catch (error) {
        console.error('Error handling place selection:', error);
      }
    },
    [onChange, onAddressSelect]
  );

  useEffect(() => {
    if (googleMaps) {
      setGoogle(googleMaps);
      setIsLoading(false);
    }
  }, [googleMaps]);

  // Sync the Google component value when the value prop changes
  useEffect(() => {
    if (autocompleteElementRef.current && value !== undefined) {
      autocompleteElementRef.current.value = value || '';
      if (containerRef.current) {
        containerRef.current.value = value || '';
      }
    }
  }, [value]);

  useEffect(() => {
    if (!google || !containerRef.current || disabled) return;

    // cleanup previous element if any
    if (autocompleteElementRef.current) {
      autocompleteElementRef.current.removeEventListener('gmp-select', handlePlaceSelect);
      autocompleteElementRef.current.parentElement?.removeChild(autocompleteElementRef.current);
    }

    // build new web-component
    const element = new google.maps.places.PlaceAutocompleteElement();
    element.types = ['address'];
    element.componentRestrictions = { country: 'us' };

    // Apply custom styling to match your app's design
    element.style.cssText = `
        width: 100%;
        height: 38px;
        padding: 0.375rem 0.75rem;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5;
        color: #212529;
        background-color: #fff;
        border: 1px solid #ced4da;
        border-radius: 0.375rem;
        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        font-family: inherit;
      `;

    // Insert the element into the DOM near the input
    const inputContainer = document.getElementById('google-autocomplete-container');
    if (inputContainer) {
      inputContainer.appendChild(element);
    } else {
      // Fallback to parent container
      const parentContainer = containerRef.current.parentElement;
      parentContainer.appendChild(element);
    }

    // Attach the correct event listener for place selection
    element.addEventListener('gmp-select', handlePlaceSelect);

    // also mirror raw input changes
    element.addEventListener('input', (e) => {
      const val = e.target?.value || '';
      containerRef.current.value = val;
      if (onChange) onChange(val);
    });

    // set initial value if any
    if (value) {
      containerRef.current.value = value;
      element.value = value;
    }
    autocompleteElementRef.current = element;

    return () => {
      element.removeEventListener('gmp-select', handlePlaceSelect);
      element.parentElement?.removeChild(element);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google, disabled, handlePlaceSelect, onChange]);

  const clearSelection = () => {
    setSelectedPlace(null);

    // Clear the Google component
    if (autocompleteElementRef.current) {
      autocompleteElementRef.current.value = '';
    }

    // Clear the hidden input
    if (containerRef.current) {
      containerRef.current.value = '';
    }

    if (onChange) {
      onChange('');
    }
    if (onAddressSelect) {
      onAddressSelect({
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
        latitude: null,
        longitude: null,
        displayName: '',
        formattedAddress: '',
      });
    }
  };

  return (
    <div className={`address-autocomplete ${className}`}>
      {/* Hidden input for form validation */}
      <input
        ref={containerRef}
        type="hidden"
        value={value || ''}
        onChange={() => { }} // Controlled input
      />

      {/* Container for the Google PlaceAutocompleteElement */}
      <div id="google-autocomplete-container" style={{ position: 'relative' }}>
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '10px',
            transform: 'translateY(-50%)',
            zIndex: 1,
            color: '#6c757d',
            fontSize: '0.875rem'
          }}>
            Loading...
          </div>
        )}
      </div>

      {/* Modern preview card showing selected place details */}
      {showPreview && selectedPlace && (
        <Card className="mt-3 border-0 shadow-sm" style={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '12px'
        }}>
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1 d-flex align-items-start">
                {/* Location Icon */}
                <div className="me-3 mt-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-primary"
                    style={{ minWidth: '20px' }}
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </div>

                {/* Location Details */}
                <div className="flex-grow-1">
                  {/* Location Name (Display Name) */}
                  {selectedPlace.displayName && (
                    <h6 className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                      {selectedPlace.displayName}
                    </h6>
                  )}

                  {/* Street Address */}
                  {selectedPlace.parsedData && selectedPlace.parsedData.address_line1 && (
                    <div className="text-muted" style={{ fontSize: '0.875rem', lineHeight: '1.4' }}>
                      {selectedPlace.parsedData.address_line1}
                      {selectedPlace.parsedData.address_line2 && (
                        <span>, {selectedPlace.parsedData.address_line2}</span>
                      )}
                      <span>, {selectedPlace.parsedData.city}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Modern Close Button */}
              <button
                type="button"
                className="btn btn-link p-0 text-muted"
                onClick={clearSelection}
                title="Clear selection"
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '1.2rem',
                  lineHeight: '1',
                  padding: '4px',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#dc3545';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#6c757d';
                }}
              >
                ✕
              </button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AddressAutocomplete; 